import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { fadeUp, viewportOnce } from "../../lib/motion";
import { trackEvent } from "../../lib/seo";
import { PRO_ROLES, PRO_INDUSTRIES, PRO_INTERESTS } from "../../lib/pro";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Field = ({ label, htmlFor, children }) => (
    <div>
        <label
            htmlFor={htmlFor}
            className="font-mono text-[13px] tracking-[0.22em] text-pro-steel-light"
        >
            {label}
        </label>
        <div className="mt-2">{children}</div>
    </div>
);

const inputCls =
    "w-full border border-pro-steel/50 bg-pro-navy/70 px-4 py-3 text-[15px] text-pro-platinum placeholder:text-pro-steel-light/60 focus:border-accent focus:outline-none";

const Pills = ({ name, options, value, onChange }) => (
    <div role="group" className="mt-2 flex flex-wrap gap-2.5">
        {options.map((o) => (
            <button
                key={o}
                type="button"
                aria-pressed={value === o}
                data-testid={`pro-${name}-${slug(o)}`}
                onClick={() => onChange(o)}
                className={`border px-4 py-2 text-[14px] transition-colors duration-300 ${
                    value === o
                        ? "border-accent bg-accent/15 text-pro-platinum"
                        : "border-pro-steel/50 text-pro-steel-light hover:border-pro-steel-light"
                }`}
            >
                {o}
            </button>
        ))}
    </div>
);

const ProEnquiryForm = () => {
    const [form, setForm] = useState({
        full_name: "",
        email: "",
        organisation: "",
        professional_role: "",
        industry: "",
        area_of_interest: "",
        consent: false,
    });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");
    const [reference, setReference] = useState("");

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (status === "sending") return;
        setError("");
        if (!form.full_name.trim() || !form.email.trim() || !form.professional_role) {
            setError("Please provide your name, email address and professional role.");
            return;
        }
        if (!form.consent) {
            setError("Please consent to being contacted about BuildEase Pro.");
            return;
        }
        setStatus("sending");
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/professional-enquiries`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(form),
                },
            );
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Submission failed");
            setReference(data.id);
            setStatus("done");
            trackEvent("pro_enquiry_submit");
        } catch (err) {
            setError(
                err.message ||
                    "Something went wrong — please try again or email support@buildease.co.za.",
            );
            setStatus("idle");
        }
    };

    return (
        <section
            id="pro-enquiry"
            data-testid="pro-enquiry-section"
            className="relative border-y border-pro-steel/30 bg-pro-blue/20 py-24 lg:py-32"
        >
            <div className="mx-auto grid max-w-[1600px] gap-14 px-6 md:px-10 lg:grid-cols-2 lg:gap-20 lg:px-16">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                >
                    <p className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-pro-steel-light">
                        <span className="h-1.5 w-1.5 bg-accent" />
                        THE PROFESSIONAL NETWORK
                    </p>
                    <h2
                        data-testid="pro-enquiry-headline"
                        className="mt-8 font-display text-3xl font-semibold leading-[1.1] text-pro-platinum md:text-5xl"
                    >
                        Join the Professional Network
                        <span className="text-accent">.</span>
                    </h2>
                    <p className="mt-6 max-w-md text-[16px] leading-relaxed text-pro-steel-light">
                        BuildEase Pro is in early development. Register your
                        interest and we will contact you as the professional
                        programme takes shape.
                    </p>
                    <p className="mt-4 max-w-md text-[14px] leading-relaxed text-pro-steel-light/80">
                        Registration expresses interest only — it does not
                        guarantee customers, leads, contracts or revenue.
                    </p>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                >
                    {status === "done" ? (
                        <div
                            data-testid="pro-success"
                            className="border border-pro-steel/50 bg-pro-navy p-8"
                        >
                            <CheckCircle2 className="h-8 w-8 text-accent" />
                            <h3 className="mt-5 font-display text-2xl font-semibold text-pro-platinum">
                                Interest registered
                            </h3>
                            <p className="mt-4 text-[15px] leading-relaxed text-pro-steel-light">
                                Thank you — your details have been captured.
                                Your reference is{" "}
                                <span
                                    data-testid="pro-reference"
                                    className="font-mono text-[13px] text-pro-platinum"
                                >
                                    {reference}
                                </span>
                                . We will reach out as the professional
                                programme develops.
                            </p>
                        </div>
                    ) : (
                        <form
                            data-testid="pro-form"
                            onSubmit={submit}
                            className="space-y-6 border border-pro-steel/50 bg-pro-navy/80 p-7 md:p-9"
                        >
                            <Field label="FULL NAME" htmlFor="pro-name">
                                <input
                                    id="pro-name"
                                    data-testid="pro-form-name"
                                    className={inputCls}
                                    value={form.full_name}
                                    onChange={(e) =>
                                        set("full_name", e.target.value)
                                    }
                                    placeholder="Your name"
                                    maxLength={200}
                                />
                            </Field>
                            <Field label="EMAIL ADDRESS" htmlFor="pro-email">
                                <input
                                    id="pro-email"
                                    data-testid="pro-form-email"
                                    type="email"
                                    className={inputCls}
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    placeholder="name@company.co.za"
                                    maxLength={200}
                                />
                            </Field>
                            <Field label="ORGANISATION" htmlFor="pro-org">
                                <input
                                    id="pro-org"
                                    data-testid="pro-form-organisation"
                                    className={inputCls}
                                    value={form.organisation}
                                    onChange={(e) =>
                                        set("organisation", e.target.value)
                                    }
                                    placeholder="Company or practice (optional)"
                                    maxLength={200}
                                />
                            </Field>
                            <Field label="PROFESSIONAL ROLE" htmlFor="pro-role">
                                <Pills
                                    name="role"
                                    options={PRO_ROLES}
                                    value={form.professional_role}
                                    onChange={(v) => set("professional_role", v)}
                                />
                            </Field>
                            <Field label="INDUSTRY" htmlFor="pro-industry">
                                <Pills
                                    name="industry"
                                    options={PRO_INDUSTRIES}
                                    value={form.industry}
                                    onChange={(v) => set("industry", v)}
                                />
                            </Field>
                            <Field
                                label="AREA OF INTEREST"
                                htmlFor="pro-interest"
                            >
                                <Pills
                                    name="interest"
                                    options={PRO_INTERESTS}
                                    value={form.area_of_interest}
                                    onChange={(v) =>
                                        set("area_of_interest", v)
                                    }
                                />
                            </Field>
                            <label className="flex items-start gap-3">
                                <input
                                    data-testid="pro-form-consent"
                                    type="checkbox"
                                    checked={form.consent}
                                    onChange={(e) =>
                                        set("consent", e.target.checked)
                                    }
                                    className="mt-1 h-4 w-4 shrink-0 accent-[#FF5B00]"
                                />
                                <span className="text-[14px] leading-relaxed text-pro-steel-light">
                                    I consent to being contacted about
                                    BuildEase Pro. See our{" "}
                                    <Link
                                        to="/privacy"
                                        className="underline hover:text-pro-platinum"
                                    >
                                        Privacy Policy
                                    </Link>
                                    .
                                </span>
                            </label>
                            {error && (
                                <p
                                    data-testid="pro-form-error"
                                    className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-[14px] text-red-300"
                                >
                                    {error}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="pro-form-submit"
                                disabled={status === "sending"}
                                className="group inline-flex w-full items-center justify-center gap-2 bg-accent px-7 py-4 text-[16px] font-semibold text-white transition-colors duration-300 hover:bg-[#ff7026] disabled:opacity-60"
                            >
                                {status === "sending"
                                    ? "Submitting…"
                                    : "Register Your Interest"}
                                {status !== "sending" && (
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                                )}
                            </button>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default ProEnquiryForm;
